import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Zap, Mic2, Layers, SlidersHorizontal, Gift, Voicemail, Youtube, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const ADVANTAGES = [
  {
    icon: Download, grad: "from-emerald-500/20 to-teal-500/10", color: "text-emerald-400", big: true,
    fr: { title: "Votre MP3 à vie", desc: "Votre musique en MP3 haute qualité, téléchargeable immédiatement. Elle vous appartient pour toute la vie : écoutez-la, partagez-la et offrez-la sans aucune limite." },
    en: { title: "Your MP3 for life", desc: "Your song as a high-quality MP3, downloadable instantly. It's yours for life: listen, share and gift it with no limits whatsoever." },
  },
  {
    icon: Zap, grad: "from-amber-500/20 to-orange-500/10", color: "text-amber-400",
    fr: { title: "Livrée en quelques minutes", desc: "Commandez, et recevez votre chanson complète en quelques minutes seulement — pas en plusieurs jours." },
    en: { title: "Delivered in minutes", desc: "Order now and receive your complete song within minutes — not days." },
  },
  {
    icon: Mic2, grad: "from-fuchsia-500/20 to-purple-500/10", color: "text-fuchsia-400",
    fr: { title: "Voix réaliste, mixée & masterisée", desc: "Une voix chantée bluffante de réalisme, disponible en plusieurs langues, avec un mixage et un mastering dignes d'un studio." },
    en: { title: "Realistic mixed & mastered vocals", desc: "Stunningly realistic sung vocals, available in multiple languages, with studio-grade mixing and mastering." },
  },
  {
    icon: Layers, grad: "from-sky-500/20 to-blue-500/10", color: "text-sky-400",
    fr: { title: "Générations multiples", desc: "Recevez jusqu'à 3 versions différentes de votre chanson et gardez celle qui vous touche le plus." },
    en: { title: "Multiple generations", desc: "Get up to 3 different versions of your song and keep the one that moves you most." },
  },
  {
    icon: SlidersHorizontal, grad: "from-violet-500/20 to-indigo-500/10", color: "text-violet-400",
    fr: { title: "Styles, rythmes & ambiances", desc: "Pop, rock, rap, country, jazz… choisissez parmi de nombreux styles, rythmes et ambiances pour une chanson qui vous ressemble." },
    en: { title: "Styles, rhythms & moods", desc: "Pop, rock, rap, country, jazz… pick from many styles, rhythms and moods for a song that truly sounds like you." },
  },
  {
    icon: Voicemail, grad: "from-rose-500/20 to-pink-500/10", color: "text-rose-400",
    fr: { title: "Votre propre voix dans la musique", desc: "Ajoutez un message vocal enregistré avec votre propre voix, intégré directement dans la chanson. Une émotion unique." },
    en: { title: "Your own voice inside the song", desc: "Add a voice message recorded with your own voice, embedded right into the song. A one-of-a-kind emotion." },
  },
  {
    icon: Youtube, grad: "from-red-500/20 to-rose-500/10", color: "text-red-400",
    fr: { title: "Publication YouTube", desc: "Mettez votre musique personnalisée en ligne sur YouTube : retrouvez-la à tout moment et partagez-la avec le monde entier." },
    en: { title: "YouTube publishing", desc: "Put your personalized song on YouTube: find it anytime and share it openly with the whole world." },
  },
  {
    icon: Gift, grad: "from-emerald-500/20 to-lime-500/10", color: "text-lime-400", big: true,
    fr: { title: "Certificat cadeau imprimable avec QR code", desc: "Offrez la chanson avec un magnifique certificat cadeau imprimable : un QR code mène directement à la musique. L'effet surprise garanti le jour J." },
    en: { title: "Printable gift certificate with QR code", desc: "Gift the song with a beautiful printable certificate: a QR code leads straight to the music. Guaranteed wow effect on the big day." },
  },
];

export default function UniqueAdvantages() {
  const { language } = useI18n();
  const isEn = language === "en";

  return (
    <section className="py-24 sm:py-28" data-testid="unique-advantages-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            {isEn ? "Why Kalypso Music" : "Pourquoi Kalypso Music"}
          </p>
          <h2 className="mt-2 font-heading text-2xl font-medium tracking-tight sm:text-3xl lg:text-4xl">
            {isEn ? "8 unique advantages, included with every song" : "8 avantages uniques, inclus avec chaque chanson"}
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {ADVANTAGES.map((a, i) => {
            const c = a[isEn ? "en" : "fr"];
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                data-testid={`advantage-card-${i + 1}`}
                className={`group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-primary/40 ${a.big ? "sm:col-span-2" : ""}`}
              >
                <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${a.grad} opacity-0 transition-opacity duration-500 group-hover:opacity-100`} />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${a.grad} ${a.color}`}>
                      <a.icon className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                    <span className="font-heading text-2xl font-semibold text-muted-foreground/20">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
                </div>
              </motion.div>
            );
          })}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.16 }}
            className="sm:col-span-2"
          >
            <Link to="/services" data-testid="advantages-services-link"
              className="group flex h-full flex-col justify-center rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/15 to-primary/5 p-6 transition-all hover:-translate-y-1 hover:border-primary/60">
              <h3 className="font-heading text-base font-semibold text-primary">
                {isEn ? "And that's not all…" : "Et ce n'est pas tout…"}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {isEn
                  ? "Custom lyrics, AI album cover, monthly plans, free trial: discover every service in detail."
                  : "Paroles sur mesure, pochette IA, abonnements mensuels, essai gratuit : découvrez chaque service en détail."}
              </p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                {isEn ? "See all our services" : "Voir tous nos services"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
